import React, { useCallback, useEffect, useState } from "react";
import { format, startOfMonth, endOfMonth, lastDayOfMonth } from "date-fns";
import { InformationCircleIcon, ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  getPayoutRuns,
  createPayoutRun,
  getPayoutLines,
  getPayoutPayments,
  type PayoutRun,
  type PayoutRunStatus,
  type PayoutLine,
  type PayoutPayment,
} from "../../apis/payoutRun";
import { getClinicUsers } from "../../apis/user";
import { useNotification } from "../../NotificationContext";

type TabType = "run_payroll" | "payroll_history";

const STATUS_LABEL: Record<PayoutRunStatus, string> = {
  draft: "Draft",
  finalized: "Finalized",
  paid: "Paid",
  cancelled: "Cancelled",
};

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  hold: "Hold",
  paid: "Paid",
  failed: "Failed",
  cancelled: "Cancelled",
};

type ClinicUser = { id: number; firstName?: string; lastName?: string };

/** Group lines by payee and attach payment status */
function groupLinesByPayee(
  lines: PayoutLine[],
  payments: PayoutPayment[],
  clinicUsers: ClinicUser[]
): { payeeId: number; payeeName: string; totalAmount: number; status: string; lines: PayoutLine[] }[] {
  const byPayee = new Map<number, PayoutLine[]>();
  for (const line of lines) {
    const id = line.payeeId;
    if (!byPayee.has(id)) byPayee.set(id, []);
    byPayee.get(id)!.push(line);
  }
  const paymentByPayee = new Map<number, PayoutPayment>();
  for (const p of payments) {
    paymentByPayee.set(p.payeeId, p);
  }
  const getUserName = (id: number) => {
    const u = clinicUsers.find((c) => c.id === id);
    return u ? [u.firstName, u.lastName].filter(Boolean).join(" ") || `User #${id}` : `User #${id}`;
  };
  return Array.from(byPayee.entries())
    .map(([payeeId, payeeLines]) => {
      const totalAmount = payeeLines.reduce((sum, l) => sum + (l.payoutAmount ?? 0), 0);
      const payment = paymentByPayee.get(payeeId);
      const status = payment?.status ?? "hold";
      return {
        payeeId,
        payeeName: getUserName(payeeId),
        totalAmount,
        status,
        lines: payeeLines,
      };
    })
    .sort((a, b) => a.payeeName.localeCompare(b.payeeName));
}

const DoctorPayouts: React.FC = () => {
  const { showNotification } = useNotification();
  const [activeTab, setActiveTab] = useState<TabType>("run_payroll");
  const [runs, setRuns] = useState<PayoutRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [detailRunId, setDetailRunId] = useState<number | null>(null);
  const [detailLines, setDetailLines] = useState<PayoutLine[]>([]);
  const [detailPayments, setDetailPayments] = useState<PayoutPayment[]>([]);
  const [clinicUsers, setClinicUsers] = useState<ClinicUser[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedPayees, setExpandedPayees] = useState<Set<number>>(new Set());

  const now = new Date();
  const periodStart = format(startOfMonth(now), "yyyy-MM-dd");
  const periodEnd = format(endOfMonth(now), "yyyy-MM-dd");
  const currentMonthLabel = format(now, "MMMM yyyy");
  const lastDay = lastDayOfMonth(now);
  const payDay = Math.min(30, lastDay.getDate());
  const payDate = format(new Date(now.getFullYear(), now.getMonth(), payDay), "dd/MM/yyyy");
  const isPastPayDay = now.getDate() > payDay;

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await getPayoutRuns({
        payoutType: "doctor",
        perPage: 100,
      });
      const data = res?.data?.data ?? res?.data ?? res;
      setRuns(Array.isArray(data) ? data : []);
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to load pay runs",
        err?.response?.data?.message ?? "",
        "topRight"
      );
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    getClinicUsers().then((res: any) => {
      const data = res?.data ?? res;
      setClinicUsers(Array.isArray(data) ? data : []);
    }).catch(() => setClinicUsers([]));
  }, []);

  const openDraftDetails = useCallback(async (runId: number) => {
    setDetailRunId(runId);
    setDetailLoading(true);
    setDetailLines([]);
    setDetailPayments([]);
    setExpandedPayees(new Set());
    try {
      const [linesRes, paymentsRes] = await Promise.all([
        getPayoutLines(runId),
        getPayoutPayments(runId),
      ]);
      const linesData = (linesRes as any)?.data?.data ?? (linesRes as any)?.data ?? [];
      const paymentsData = (paymentsRes as any)?.data?.data ?? (paymentsRes as any)?.data ?? [];
      setDetailLines(Array.isArray(linesData) ? linesData : []);
      setDetailPayments(Array.isArray(paymentsData) ? paymentsData : []);
    } catch (err: any) {
      showNotification("error", "Failed to load payout details", err?.response?.data?.message ?? "", "topRight");
    } finally {
      setDetailLoading(false);
    }
  }, [showNotification]);

  const closeDraftDetails = useCallback(() => {
    setDetailRunId(null);
    setDetailLines([]);
    setDetailPayments([]);
    setExpandedPayees(new Set());
  }, []);

  const togglePayeeExpanded = useCallback((payeeId: number) => {
    setExpandedPayees((prev) => {
      const next = new Set(prev);
      if (next.has(payeeId)) next.delete(payeeId);
      else next.add(payeeId);
      return next;
    });
  }, []);

  const currentRun = runs.find(
    (r) => r.periodStart <= periodEnd && r.periodEnd >= periodStart && r.status !== "cancelled"
  );
  const summary = currentRun?.summary ?? {};
  const doctorCount = summary.doctorCount ?? 0;
  const netPay = summary.netPay;
  const netPayDisplay =
    netPay != null && netPay !== "" ? `₹${Number(netPay).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "YET TO PROCESS";
  const status: PayoutRunStatus = currentRun?.status ?? "draft";
  const statusLabel = currentRun ? STATUS_LABEL[status] : "READY";

  const handleCreatePayRun = async () => {
    if (currentRun) {
      showNotification("info", "A pay run already exists for this period", "", "topRight");
      return;
    }
    setCreating(true);
    try {
      await createPayoutRun({
        payoutType: "doctor",
        periodStart,
        periodEnd,
        status: "draft",
      });
      showNotification("success", "Pay run created", "", "topRight");
      fetchRuns();
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to create pay run",
        err?.response?.data?.message ?? "",
        "topRight"
      );
    } finally {
      setCreating(false);
    }
  };

  const historyRuns = runs
    .filter((r) => r.payoutType === "doctor")
    .sort((a, b) => (b.periodStart || "").localeCompare(a.periodStart || ""));

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex-auto">
        <h1 className="text-base font-semibold leading-6 text-gray-900">
          Doctor Payouts
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          Generate pay runs and manage doctor payout history.
        </p>
      </div>

      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-8" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab("run_payroll")}
            className={`whitespace-nowrap border-b-2 py-4 text-sm font-medium ${
              activeTab === "run_payroll"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
          >
            Run Payroll
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("payroll_history")}
            className={`whitespace-nowrap border-b-2 py-4 text-sm font-medium ${
              activeTab === "payroll_history"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
          >
            Payroll History
          </button>
        </nav>
      </div>

      {activeTab === "run_payroll" && (
        <div className="mt-6">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="p-6">
              {loading ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : (
                <>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-base font-semibold text-gray-900">
                        Process Pay Run for {currentMonthLabel}
                      </h2>
                      <span className="inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 text-xs font-medium text-white">
                        {statusLabel}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {currentRun && status === "draft" && (
                        <button
                          type="button"
                          onClick={() => openDraftDetails(currentRun.id)}
                          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        >
                          View details
                        </button>
                      )}
                      {!currentRun && (
                        <button
                          type="button"
                          onClick={handleCreatePayRun}
                          disabled={creating}
                          className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50"
                        >
                          {creating ? "Creating..." : "Create Pay Run"}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-8">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Doctors&apos; Net Pay
                      </p>
                      <p className="mt-1 text-base font-semibold text-gray-900">
                        {netPayDisplay}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Payment Date
                      </p>
                      <p className="mt-1 text-base font-semibold text-gray-900">
                        {payDate}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        No. of Doctors
                      </p>
                      <p className="mt-1 text-base font-semibold text-gray-900">
                        {doctorCount}
                      </p>
                    </div>
                  </div>

                  {isPastPayDay && !currentRun && (
                    <div className="mt-6 flex items-start gap-2 rounded-md bg-gray-50 px-3 py-2">
                      <InformationCircleIcon className="h-5 w-5 shrink-0 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        You haven&apos;t processed this pay run and it&apos;s past the pay day.
                      </p>
                    </div>
                  )}

                  {detailRunId !== null && detailRunId === currentRun?.id && (
                    <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900">Payout details (grouped by payee)</h3>
                        <button
                          type="button"
                          onClick={closeDraftDetails}
                          className="text-sm font-medium text-gray-600 hover:text-gray-900"
                        >
                          Close
                        </button>
                      </div>
                      {detailLoading ? (
                        <p className="mt-4 text-sm text-gray-500">Loading lines and payments...</p>
                      ) : (
                        <div className="mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                          <table className="min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="w-8 py-3 pl-4 pr-2 text-left text-sm font-semibold text-gray-900 sm:pl-6" />
                                <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold text-gray-900">
                                  Payee
                                </th>
                                <th className="px-3 py-3 text-right text-sm font-semibold text-gray-900">
                                  Amount
                                </th>
                                <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {(() => {
                                const grouped = groupLinesByPayee(detailLines, detailPayments, clinicUsers);
                                if (grouped.length === 0) {
                                  return (
                                    <tr>
                                      <td colSpan={4} className="py-8 text-center text-sm text-gray-500">
                                        No payout lines yet. Add visits/services to generate lines.
                                      </td>
                                    </tr>
                                  );
                                }
                                return grouped.map((row) => {
                                  const isExpanded = expandedPayees.has(row.payeeId);
                                  return (
                                    <React.Fragment key={row.payeeId}>
                                      <tr className="bg-white hover:bg-gray-50">
                                        <td className="w-8 py-2 pl-4 pr-2 sm:pl-6">
                                          {row.lines.length > 0 ? (
                                            <button
                                              type="button"
                                              onClick={() => togglePayeeExpanded(row.payeeId)}
                                              className="text-gray-500 hover:text-gray-700"
                                            >
                                              {isExpanded ? (
                                                <ChevronDownIcon className="h-4 w-4" />
                                              ) : (
                                                <ChevronRightIcon className="h-4 w-4" />
                                              )}
                                            </button>
                                          ) : null}
                                        </td>
                                        <td className="py-2 pl-2 pr-3 text-sm font-medium text-gray-900">
                                          {row.payeeName}
                                        </td>
                                        <td className="px-3 py-2 text-right text-sm text-gray-900">
                                          ₹{row.totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-3 py-2">
                                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                                            {PAYMENT_STATUS_LABEL[row.status] ?? row.status}
                                          </span>
                                        </td>
                                      </tr>
                                      {isExpanded &&
                                        row.lines.map((line) => (
                                          <tr key={line.id} className="bg-gray-50/80">
                                            <td className="w-8 py-1.5 pl-4 pr-2 sm:pl-6" />
                                            <td className="py-1.5 pl-8 pr-3 text-xs text-gray-600">
                                              Line #{line.id}
                                              {line.serviceDate != null && ` · ${format(new Date(line.serviceDate), "dd MMM yyyy")}`}
                                              {line.sourceType != null && ` · ${line.sourceType}`}
                                            </td>
                                            <td className="px-3 py-1.5 text-right text-xs text-gray-600">
                                              ₹{(line.payoutAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="px-3 py-1.5" />
                                          </tr>
                                        ))}
                                    </React.Fragment>
                                  );
                                });
                              })()}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "payroll_history" && (
        <div className="mt-6">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="p-6">
              {loading ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          Period
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Payment Date
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          No. of Doctors
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Net Pay
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {historyRuns.length > 0 ? (
                        historyRuns.map((run) => {
                          const s = run.summary ?? {};
                          const count = s.doctorCount ?? 0;
                          const net = s.netPay;
                          const netStr =
                            net != null && net !== ""
                              ? `₹${Number(net).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                              : "—";
                          const periodLabel =
                            run.periodStart && run.periodEnd
                              ? `${format(new Date(run.periodStart), "MMM d, yyyy")} – ${format(new Date(run.periodEnd), "MMM d, yyyy")}`
                              : "—";
                          const paidAt = run.paidAt;
                          const paymentDate = paidAt
                            ? format(new Date(paidAt), "dd/MM/yyyy")
                            : payDate;
                          return (
                            <tr key={run.id}>
                              <td className="py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6">
                                {periodLabel}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-500">
                                {paymentDate}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-500">
                                {count}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-500">
                                {netStr}
                              </td>
                              <td className="px-3 py-4">
                                <span className="inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 text-xs font-medium text-white">
                                  {STATUS_LABEL[run.status as PayoutRunStatus] ?? run.status}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan={5}
                            className="py-8 text-center text-sm text-gray-500"
                          >
                            No payroll history yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorPayouts;
